import React, { useState, useEffect } from 'react';
import { Form, CardGroup, Card, CardBody, Row, Col, FormGroup, Label, InputGroup } from 'reactstrap';
import { FormattedMessage, useIntl } from 'react-intl';
import PhoneInput from 'react-phone-number-input';
import { get } from 'lodash-es';
import { CustomDashboardInput, CustomButton } from '../../containers';
import { validateFormSubmission } from '../../utils/validation';
import { USER_ACCOUNT_TYPES } from '../../constants/userDefs';
import { formatPhoneNumber } from '../../utils/formatPhoneNumber';
import { PlaceAndZipInput } from '../PlaceAndZipInput';

export const AdminEditBusinessUserProfileData = ({
  user,
  inputHandler,
  toggleAllowEditModal,
  allowEdit,
  places,
  countries,
  updateProfileInfo,
  loading,
  loadingPlaces,
  loadingCountries,
}) => {
  const { formatMessage } = useIntl();
  const [submitted, setSubmitted] = useState(false);

  const [businessFirstName, setBusinessFirstName] = useState({
    value: user.businessFirstName || '',
    error: null,
    invalid: true,
    focused: false,
    displayName: <FormattedMessage id="inputs.firstName" />,
    validation: {},
  });
  const [businessLastName, setBusinessLastName] = useState({
    value: user.businessLastName || '',
    error: null,
    invalid: true,
    focused: false,
    displayName: <FormattedMessage id="inputs.lastName" />,
    validation: {},
  });
  const [OIB, setOIB] = useState({
    value: user.oib || '',
    error: null,
    invalid: true,
    focused: false,
    displayName: <FormattedMessage id="inputs.OIB" />,
    validation: {
      required: true,
      maxLength: 11,
      minLength: 11,
      oib: true,
    },
  });
  const [mbo, setMbo] = useState({
    value: user.mbo || '',
    error: null,
    invalid: true,
    focused: false,
    displayName: <FormattedMessage id="inputs.mbo" />,
    validation: {},
  });
  const [businessName, setBusinessName] = useState({
    value: user.businessName || '',
    error: null,
    invalid: true,
    focused: false,
    displayName: <FormattedMessage id="inputs.name" />,
    validation: {
      required: true,
    },
  });
  const [VATpayer, setVATpayer] = useState({
    value: user.VATpayer || false,
    error: null,
    invalid: true,
    focused: false,
    displayName: <FormattedMessage id="inputs.VATpayer" />,
    validation: {
      required: true,
    },
  });
  const [address, setAddress] = useState({
    value: user.address || '',
    error: null,
    invalid: true,
    focused: false,
    displayName: <FormattedMessage id="inputs.address" />,
    validation: {
      required: true,
    },
  });
  const [city, setCity] = useState({
    value: user.city || '',
    error: null,
    invalid: true,
    focused: false,
    displayName: <FormattedMessage id="inputs.city" />,
    validation: {
      required: true,
      enum: places.map(place => place.name),
    },
  });
  const [zip, setZip] = useState({
    value: user.zip || '',
    error: null,
    invalid: true,
    focused: false,
    displayName: <FormattedMessage id="inputs.zipCode" />,
    validation: {
      required: true,
      enum: places.map(place => place.pttNumber),
    },
  });
  const [sector, setSector] = useState({
    value: user.sector || '',
    error: null,
    invalid: false,
    displayName: <FormattedMessage id="inputs.sector" />,
    validation: {
      required: true,
      enum: USER_ACCOUNT_TYPES.map(type => type.name),
    },
  });
  const [country, setCountry] = useState({
    value: countries.some(country => country.codeLegacy === user.country) ? user.country : '',
    error: null,
    invalid: true,
    focused: false,
    displayName: <FormattedMessage id="inputs.country" />,
    validation: {
      required: true,
      enum: countries.map(country => country.codeLegacy),
    },
  });
  const [businessTelephone, setBusinessTelephone] = useState({
    value: user.businessTelephone || '',
    error: null,
    invalid: false,
    displayName: <FormattedMessage id="inputs.businessTelephone" />,
    validation: {},
  });
  const [webAddress, setWebAddress] = useState({
    value: user.webAddress || '',
    error: null,
    invalid: false,
    displayName: <FormattedMessage id="inputs.webAddress" />,
    validation: {},
  });

  useEffect(() => {
    if (places.length > 0 && city.validation.enum.length < 1) {
      setCity(o => ({ ...o, validation: { ...o.validation, enum: places.map(place => place.name) } }));
    }
    if (places.length > 0 && zip.validation.enum.length < 1) {
      setZip(o => ({ ...o, validation: { ...o.validation, enum: places.map(place => place.pttNumber) } }));
    }

    if (countries.length > 0 && country.validation.enum.length < 1) {
      setCountry(o => ({
        ...o,
        value: countries.some(country => country.codeLegacy === user.country) ? user.country : '',
        validation: { ...o.validation, enum: countries.map(country => country.codeLegacy) },
      }));
    }
  }, [places, countries, city, country, user.country, zip]);

  const handleUserProfileDataChange = async event => {
    event.preventDefault();
    setSubmitted(true);
    const fieldsArray = [
      setOIB,
      setMbo,
      setBusinessName,
      setVATpayer,
      setAddress,
      setCity,
      setZip,
      setCountry,
      setBusinessFirstName,
      setBusinessLastName,
      setBusinessTelephone,
      setWebAddress,
    ];
    !user.sector && fieldsArray.push(setSector);

    if (await validateFormSubmission(fieldsArray)) {
      updateProfileInfo({
        variables: {
          id: user.id,
          email: user.email,
          phoneNumber: user.phoneNumber,
          ...(!user.sector && { sector: sector.value }),
          oib: OIB.value,
          mbo: mbo.value,
          businessName: businessName.value,
          VATpayer: VATpayer.value,
          address: address.value,
          city: city.value,
          zip: zip.value,
          country: country.value,
          businessFirstName: businessFirstName.value,
          businessLastName: businessLastName.value,
          businessTelephone: businessTelephone.value,
          webAddress: webAddress.value,
        },
      });
    }
  };

  const disableSubmitBtn = () => {
    return (
      !allowEdit ||
      loading ||
      (sector.value === (get(user, 'sector', '') || '') &&
        businessName.value === (get(user, 'businessName', '') || '') &&
        OIB.value === (get(user, 'oib', '') || '') &&
        mbo.value === (get(user, 'mbo', '') || '') &&
        VATpayer.value === (get(user, 'VATpayer', '') || false) &&
        address.value === (get(user, 'address', '') || '') &&
        city.value === (get(user, 'city', '') || '') &&
        zip.value === (get(user, 'zip', '') || '') &&
        businessFirstName.value === (get(user, 'businessFirstName', '') || '') &&
        businessLastName.value === (get(user, 'businessLastName', '') || '') &&
        businessTelephone.value === (get(user, 'businessTelephone', '') || '') &&
        webAddress.value === (get(user, 'webAddress', '') || ''))
    );
  };

  return (
    <Form onSubmit={handleUserProfileDataChange}>
      <CardGroup>
        <Card className="hok-card">
          <CardBody>
            <Row className="my-4">
              <Col xs="12">
                <Row className="no-gutters mb-3 mb-sm-0">
                  <Col className="text-right" xs="12">
                    <CustomButton
                      className={allowEdit ? 'hok-dashboard-btn' : 'hok-outline-btn'}
                      outline={!allowEdit}
                      block={false}
                      translationId="changeData"
                      disabled={allowEdit}
                      onClick={event => {
                        event.preventDefault();
                        toggleAllowEditModal();
                      }}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col xs="12">
                    <h3>
                      <FormattedMessage id="adminEditUserPage.basicInfo" />
                    </h3>
                  </Col>
                </Row>
                <FormGroup row className="mt-4">
                  <Col sm="6">
                    <Label for="OIB">
                      <FormattedMessage id="inputs.OIB" />
                    </Label>
                    <InputGroup>
                      <CustomDashboardInput
                        id="OIB"
                        disabled={!allowEdit}
                        value={OIB.value}
                        fieldState={OIB}
                        formSubmitted={submitted}
                        onChange={event => inputHandler(setOIB, event.target.value)}
                      />
                    </InputGroup>
                  </Col>
                  <Col sm="6">
                    <Label for="MBO">
                      <FormattedMessage id="inputs.MBO" />
                    </Label>
                    <InputGroup>
                      <CustomDashboardInput
                        id="MBO"
                        disabled={!allowEdit}
                        value={mbo.value}
                        fieldState={mbo}
                        formSubmitted={submitted}
                        onChange={event => inputHandler(setMbo, event.target.value)}
                      />
                    </InputGroup>
                  </Col>
                </FormGroup>
                <FormGroup row>
                  <Col sm="6">
                    <Label for="name">
                      <FormattedMessage id="inputs.name" />
                    </Label>
                    <InputGroup>
                      <CustomDashboardInput
                        id="name"
                        disabled={!allowEdit}
                        value={businessName.value}
                        fieldState={businessName}
                        formSubmitted={submitted}
                        onChange={event => inputHandler(setBusinessName, event.target.value)}
                      />
                    </InputGroup>
                  </Col>
                  <Col sm="6">
                    <Label for="VATpayer">
                      <FormattedMessage id="inputs.VATpayer" />
                    </Label>
                    <InputGroup>
                      <div className="hok-select">
                        <CustomDashboardInput
                          id="VATpayer"
                          type="select"
                          disabled={!allowEdit}
                          value={VATpayer.value}
                          fieldState={VATpayer}
                          formSubmitted={submitted}
                          onChange={event => {
                            const VATpayer = event.target.value === 'true';
                            inputHandler(setVATpayer, VATpayer);
                          }}>
                          <option value={true}>{formatMessage({ id: 'yes' }).toUpperCase()}</option>
                          <option value={false}>{formatMessage({ id: 'no' }).toUpperCase()}</option>
                        </CustomDashboardInput>
                      </div>
                    </InputGroup>
                  </Col>
                </FormGroup>
                <FormGroup row>
                  <Col sm="6">
                    <Label for="address">
                      <FormattedMessage id="inputs.address" />
                    </Label>
                    <InputGroup>
                      <CustomDashboardInput
                        id="address"
                        disabled={!allowEdit}
                        value={address.value}
                        fieldState={address}
                        formSubmitted={submitted}
                        onChange={event => inputHandler(setAddress, event.target.value)}
                      />
                    </InputGroup>
                  </Col>
                  <PlaceAndZipInput
                    places={places}
                    disabled={!allowEdit}
                    formSubmitted={submitted}
                    loadingPlaces={loadingPlaces}
                    zip={zip}
                    setZip={setZip}
                    city={city}
                    setCity={setCity}
                  />
                  <Col sm="6">
                    <Label for="country">
                      <FormattedMessage id="inputs.country" />
                    </Label>
                    <InputGroup>
                      <div className="hok-select">
                        <CustomDashboardInput
                          id="country"
                          type="select"
                          disabled={!!country.value || (!allowEdit && !country.value)}
                          value={country.value}
                          fieldState={country}
                          formSubmitted={submitted}
                          onChange={event => inputHandler(setCountry, event.target.value)}>
                          <option disabled value=""></option>
                          {[{ codeLegacy: 'HR', nameCro: 'HRVATSKA' }].map(country => (
                            <option key={country.codeLegacy} value={country.codeLegacy}>
                              {country.nameCro}
                            </option>
                          ))}
                        </CustomDashboardInput>
                      </div>
                    </InputGroup>
                  </Col>
                </FormGroup>
                <FormGroup row>
                  <Col sm="6" className="mb-3">
                    <Label for="sector">
                      <FormattedMessage id="inputs.sector" />
                    </Label>
                    <InputGroup>
                      <div className="hok-select">
                        <CustomDashboardInput
                          className="text-uppercase"
                          type="select"
                          id="sector"
                          disabled={!allowEdit || user.sector}
                          fieldState={sector}
                          onChange={event => inputHandler(setSector, event.target.value)}>
                          <option disabled value=""></option>
                          {USER_ACCOUNT_TYPES.map(type => (
                            <option key={type.name} value={type.name}>
                              {formatMessage({ id: `adminCreateUserPage.filter.${type.name}` })}
                            </option>
                          ))}
                        </CustomDashboardInput>
                      </div>
                    </InputGroup>
                  </Col>
                  <Col sm="6">
                    <Label for="webAddress">
                      <FormattedMessage id="inputs.webAddress" />
                    </Label>
                    <InputGroup>
                      <CustomDashboardInput
                        id="webAddress"
                        name="web address"
                        disabled={!allowEdit}
                        placeholder={true}
                        fieldState={webAddress}
                        onChange={event => inputHandler(setWebAddress, event.target.value)}
                      />
                    </InputGroup>
                  </Col>
                </FormGroup>
                <Row className="my-4">
                  <Col xs="12">
                    <h3>
                      <FormattedMessage id="adminEditUserPage.contactInfo" />
                    </h3>
                  </Col>
                </Row>
                <FormGroup row>
                  <Col sm="6">
                    <Label for="firstName">
                      <FormattedMessage id="inputs.firstName" />
                    </Label>
                    <InputGroup>
                      <CustomDashboardInput
                        id="firstName"
                        disabled={!allowEdit}
                        value={businessFirstName.value}
                        fieldState={businessFirstName}
                        formSubmitted={submitted}
                        placeholder={true}
                        onChange={event => inputHandler(setBusinessFirstName, event.target.value)}
                      />
                    </InputGroup>
                  </Col>
                  <Col sm="6">
                    <Label for="lastName">
                      <FormattedMessage id="inputs.lastName" />
                    </Label>
                    <InputGroup>
                      <CustomDashboardInput
                        id="lastName"
                        disabled={!allowEdit}
                        value={businessLastName.value}
                        fieldState={businessLastName}
                        formSubmitted={submitted}
                        placeholder={true}
                        onChange={event => inputHandler(setBusinessLastName, event.target.value)}
                      />
                    </InputGroup>
                  </Col>
                </FormGroup>
                <FormGroup row>
                  <Col sm="6">
                    <Label for="businessTelephone">
                      <FormattedMessage id="inputs.businessTelephone" />
                    </Label>
                    <InputGroup>
                      {/* <CustomDashboardInput
                        id="businessTelephone"
                        name="business telephone"
                        disabled={!allowEdit}
                        placeholder={true}
                        fieldState={businessTelephone}
                        onChange={event => inputHandler(setBusinessTelephone, event.target.value)}
                      /> */}
                      <CustomDashboardInput
                        id="businessTelephone"
                        name="business telephone"
                        className={!allowEdit ? 'disabled' : ''}
                        disabled={!allowEdit}
                        type="tel"
                        maxLength={21}
                        tag={PhoneInput}
                        addInternationalOption={false}
                        defaultCountry="HR"
                        fieldState={businessTelephone}
                        numberInputProps={businessTelephone.invalid ? 'is-invalid' : ''}
                        onChange={phone => inputHandler(setBusinessTelephone, formatPhoneNumber(phone))}
                      />
                    </InputGroup>
                  </Col>
                </FormGroup>
              </Col>
            </Row>
          </CardBody>
        </Card>
      </CardGroup>
      <Row className="no-gutters mt-5">
        <Col className="text-right" xs="12">
          <CustomButton
            className="hok-dashboard-btn"
            block={false}
            translationId="save"
            loaderProp={loading}
            disabled={disableSubmitBtn()}
          />
        </Col>
      </Row>
    </Form>
  );
};
